/**
 * Env-scoped localStorage for session/auth state.
 *
 * The problem this solves: every build served from the same origin
 * shares one `localStorage`. When a *prod* build is (accidentally)
 * deployed to a dev host, then replaced by the correct *dev* build, the
 * prod build's records survive — and the dev build happily reads a prod
 * `api-host` (`https://ez-qr.online`) out of them, aiming auth at the
 * wrong backend. The host allow-list (`env.prodApiHosts`) can't catch
 * this because the prod hosts are shipped into the dev build too, so the
 * prod value passes validation.
 *
 * Fix: prefix env-coupled keys with the build's `VITE_ENV`. A `dev`
 * build writes/reads `ezqr:dev:*`; a `prod` build uses `ezqr:prod:*`.
 * Neither can ever see the other's records, so a leftover record from
 * the wrong build is simply not found and the caller falls back to the
 * env default — silent self-heal, no manual cache clear needed.
 *
 * Scope this ONLY to state that is meaningfully tied to a backend
 * environment (api host, CSRF, selected site). UX preferences like
 * theme/accent are intentionally build-agnostic and must NOT be scoped
 * (they're also read by the pre-Vue splash, which has no env context).
 *
 * localStorage access is wrapped in try/catch: it throws in private-mode
 * Safari and when storage is disabled, and a session helper must never
 * take the whole boot down over a preference store.
 */
import { env } from '@/app/config/env'

/** All env-scoped keys live under this prefix, e.g. `ezqr:dev:api-host`. */
const SCOPE_PREFIX = `ezqr:${env.envName}:`

function scopedKey(key: string): string {
  return `${SCOPE_PREFIX}${key}`
}

export const scopedStorage = {
  /** Read a scoped value, or `null` if absent / storage unavailable. */
  get(key: string): string | null {
    if (typeof localStorage === 'undefined') return null
    try {
      return localStorage.getItem(scopedKey(key))
    } catch {
      return null
    }
  },

  /** Write a scoped value, or remove it when `value` is `null`. */
  set(key: string, value: string | null): void {
    if (typeof localStorage === 'undefined') return
    try {
      if (value === null) localStorage.removeItem(scopedKey(key))
      else localStorage.setItem(scopedKey(key), value)
    } catch {
      // ignore — storage disabled / quota / private mode
    }
  },

  /** Remove a single scoped key. */
  remove(key: string): void {
    this.set(key, null)
  },
} as const

/**
 * One-shot cleanup of the pre-scoping key layout. Before env-scoping,
 * the session/site keys were written unscoped (`ezqr:api-host`,
 * `ezqr:csrf`, `ezqr:current-site-id:*`). After this change those keys
 * are simply never read, so they'd linger as dead records forever.
 *
 * We remove them at boot. There's no value migration on purpose: the
 * data they held (host, CSRF, selected site) re-establishes itself on
 * the next login / silent refresh, so dropping it just sends the user
 * through the normal restore path under the correct env scope. Safe to
 * run on every boot — it's a no-op once the legacy keys are gone.
 */
export function migrateLegacyKeys(): void {
  if (typeof localStorage === 'undefined') return
  try {
    const legacy: string[] = []
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i)
      if (!k) continue
      if (k === 'ezqr:api-host' || k === 'ezqr:csrf' || k.startsWith('ezqr:current-site-id:')) {
        legacy.push(k)
      }
    }
    for (const k of legacy) localStorage.removeItem(k)
  } catch {
    // ignore — storage unavailable
  }
}

/**
 * Wipe every `ezqr:*` localStorage record — both the current env's
 * scoped keys AND any legacy/unscoped or other-env leftovers. Used by
 * the reset escape hatch (`?reset` boot flag / dev control) to recover
 * from a poisoned cache in one shot. Preferences (theme/accent) are
 * `ezqr:`-prefixed too, so a full reset clears them as well — that's
 * acceptable for an explicit "reset everything" action.
 */
export function clearAllEzqrStorage(): number {
  if (typeof localStorage === 'undefined') return 0
  let removed = 0
  try {
    const keys: string[] = []
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i)
      if (k && k.startsWith('ezqr:')) keys.push(k)
    }
    for (const k of keys) {
      localStorage.removeItem(k)
      removed += 1
    }
  } catch {
    // ignore — nothing we can do if storage is unavailable
  }
  return removed
}
